import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoubtHistoryPage({ token }) {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const loadDoubts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/doubt-history/recent`, { headers: headers() });
      const data = await res.json();
      if (data.success) setDoubts(data.doubts || data.history || data.recent || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadDoubts(); }, [loadDoubts]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="doubt-history-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <i className="fa-solid fa-circle-question text-amber-500"></i> Doubt History
          </h1>
          <p className="text-sm text-gray-500">Students' doubt solving records & analysis</p>
        </div>
        <button onClick={loadDoubts} className="p-2 hover:bg-gray-100 rounded-lg transition" data-testid="doubts-refresh-btn">
          <i className="fa-solid fa-arrows-rotate text-gray-500"></i>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Doubts', value: doubts.length, icon: 'fa-circle-question', color: 'from-amber-500 to-amber-600' },
          { label: 'Resolved', value: doubts.filter(d => d.status === 'resolved').length, icon: 'fa-check-circle', color: 'from-emerald-500 to-emerald-600' },
          { label: 'Pending', value: doubts.filter(d => d.status === 'pending').length, icon: 'fa-clock', color: 'from-orange-500 to-orange-600' },
          { label: 'AI Answered', value: doubts.filter(d => d.answered_by === 'ai').length, icon: 'fa-robot', color: 'from-violet-500 to-violet-600' },
        ].map((s, i) => (
          <div key={i} className={`bg-gradient-to-br ${s.color} rounded-xl p-4 text-white`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs opacity-80">{s.label}</p>
              <i className={`fa-solid ${s.icon} text-sm opacity-60`}></i>
            </div>
            <p className="text-2xl font-bold">{s.value}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : doubts.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-16 text-center">
          <i className="fa-solid fa-circle-question text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500 font-medium">अभी कोई doubt record नहीं है</p>
          <p className="text-gray-400 text-sm mt-1">Students के doubts यहाँ दिखेंगे</p>
        </div>
      ) : (
        <div className="space-y-3">
          {doubts.map((d, i) => (
            <div key={d.doubt_id || i} className="bg-white rounded-xl border p-4 hover:shadow-sm transition" data-testid={`doubt-${i}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${d.status === 'resolved' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  <i className={`fa-solid ${d.status === 'resolved' ? 'fa-check-circle text-emerald-500' : 'fa-clock text-amber-500'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">{d.question || d.title || 'Doubt'}</p>
                  {d.answer && <p className="text-sm text-gray-500 mt-1">{d.answer}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {d.student_name && <span><i className="fa-solid fa-user mr-1"></i>{d.student_name}</span>}
                    {d.subject && <span><i className="fa-solid fa-book mr-1"></i>{d.subject}</span>}
                    {d.answered_by && <span><i className="fa-solid fa-robot mr-1"></i>{d.answered_by}</span>}
                    {d.created_at && <span><i className="fa-regular fa-clock mr-1"></i>{new Date(d.created_at).toLocaleString('hi-IN')}</span>}
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${d.status === 'resolved' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300'}`}>
                  {d.status || 'pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
